import { Flex, Text } from '@chakra-ui/react'
import { t } from 'i18next'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import React, { ReactNode } from 'react'
import { GlobalHotKeys } from '~/atoms/components/GlobalHotKeys'
import { NotificationBadgeVariant } from '~/components/shared/componentStyles/NotificationBadge.style'

import { ObjectValues } from '@mangovoice/types'

import { StorageKeys } from './StorageKeys'

export interface ExperimentState {
  integrationsEnabled: boolean
  globalPhoneCapture: {
    enabled: boolean
    keyCombo?: string
  }
}

const defaultExperimentState: ExperimentState = {
  integrationsEnabled: false,
  globalPhoneCapture: {
    enabled: false,
  },
}

export const experimentStateAtom = atomWithStorage<ExperimentState>(
  StorageKeys.Experiments,
  defaultExperimentState,
)

interface BasicExperiment {
  enabled: boolean
  title: string
  description?: string
  internalOnly: boolean
  availableOn: AvailableOn
  onSet: (enabled: boolean) => void
  onLoad?: () => void
  children?: ReactNode
  badge?: {
    text: string
    styleVariant: NotificationBadgeVariant
  }
}

export const AVAILABLE_ON = {
  ALL: 'all',
  DESKTOP: 'desktop',
  WEB: 'web',
} as const

export type AvailableOn = ObjectValues<typeof AVAILABLE_ON>

export const useExperimentsState = () => {
  const [experimentState, setExperimentState] = useAtom(experimentStateAtom)

  const globalPhoneNumberExperiment: BasicExperiment = {
    enabled: experimentState.globalPhoneCapture?.enabled ?? false,
    title: t('experimentsState.globalPhoneCapture.title'),
    badge: {
      text: t('experimentsState.badge.beta'),
      styleVariant: NotificationBadgeVariant.Beta,
    },
    internalOnly: true,
    availableOn: AVAILABLE_ON.DESKTOP,
    onSet: (enabled: boolean) => {
      setExperimentState((prev) => ({
        ...prev,
        globalPhoneCapture: {
          enabled,
        },
      }))
    },
    children: (
      <Flex flexDirection={'column'} color="fg.muted">
        <Text>
          {t('experimentsState.globalPhoneCapture.description.intro')}
        </Text>
        <Text marginTop={'1rem'}>
          {t('experimentsState.globalPhoneCapture.description.howToUse')}
        </Text>
        <Text marginTop={'1rem'}>
          {t('experimentsState.globalPhoneCapture.description.afterSet')}
        </Text>
        <Flex marginTop={'1rem'}>
          <GlobalHotKeys />
        </Flex>
        <hr />
        <Text fontStyle={'italic'}>
          {t('experimentsState.globalPhoneCapture.description.note')}
        </Text>
      </Flex>
    ),
  }

  const integrationsExperiment: BasicExperiment = {
    enabled: experimentState.integrationsEnabled ?? false,
    title: 'Integrations',
    description:
      'Enable integration agent management to authorize data source connections',
    onSet: (enabled: boolean) => {
      setExperimentState((prev) => ({
        ...prev,
        integrationsEnabled: enabled,
      }))
    },
    internalOnly: false,
    availableOn: AVAILABLE_ON.ALL,
    badge: {
      text: 'Beta',
      styleVariant: NotificationBadgeVariant.Beta,
    },
  }

  const experiments: BasicExperiment[] = [
    globalPhoneNumberExperiment,
    integrationsExperiment,
  ]

  return {
    experiments,
    experimentState,
    actions: {},
  }
}
